// @ts-strict-ignore
import * as React from 'react';
import { matchSorter } from 'match-sorter';
import { FormattedMessage } from 'react-intl';
import { tss } from 'tss-react/mui';
import {
    Button,
    Checkbox,
    ListItemText,
    MenuItem,
    styled,
} from '@mui/material';
import type { SelectProps } from '@mui/material';
import { getThemeColor } from '@/muiTheme';
import { darkModeBackgroundImageElevation } from '@/utils/darkModeBackgroundImageElevation';
import MultipleSelect from '../MultipleSelect';
import { SearchIcon } from '../icons';

// TODO:
// - If the title of the project is very long, the width of the list item also adopt the text width. This
//   creates an issue for the menu container where the width of menu container depends on the longest width of the
//   list item. This makes the menu width longer than its associated select input. Our solution for this is
//   to set a `max-width` either on the `text` or in the list item and make the text wrap to next line.

export type SelectOption = { label: string; value: string };
export type SelectOptions = Array<SelectOption>;

const SearchBarRoot = styled('div')({
    '& > *': {
        marginBottom: 15,

        '&:last-child': {
            marginBottom: 0,
        },
    },
});
const SearchTextFieldIconContainer = styled('span')(({ theme }) => ({
    color: getThemeColor(theme)('darkGray'),
    position: 'relative',
    top: 3,
}));

const SearchTextFieldContainer = styled('div')({
    boxShadow: ' 0 8px 16px 0 rgba(0, 0, 0, 0.08)',
    border: ' solid 1px #e6e6e6',
    //background: 'transparent',//'rgba(210, 213, 224, 0.1)',
    padding: '13px 16px',
    borderRadius: '4px',
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    columnGap: 12,
    alignItems: 'center',
});
const SearchTextField = styled('input')(({ theme }) => ({
    background: 'none',
    border: 0,
    outline: 0,
    fontSize: 18,
    color: theme.palette.text.primary,
}));

function SearchBar({
    value,
    onChange,
    placeholder,
    children,
    ...inputProps
}: {
    placeholder: string;
    children: React.ReactNode;
    value: string;
    onChange: React.ChangeEventHandler<HTMLInputElement>;
}) {
    return (
        // Need to disable the propagation of the events to be able
        // we can avoid the default Select menu item behaviour when clicking this
        // element.
        <SearchBarRoot
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
        >
            <SearchTextFieldContainer>
                <SearchTextFieldIconContainer>
                    <SearchIcon size={18} />
                </SearchTextFieldIconContainer>
                <SearchTextField
                    {...inputProps}
                    type="text"
                    value={value}
                    placeholder={placeholder}
                    onChange={onChange}
                />
            </SearchTextFieldContainer>
            {children}
        </SearchBarRoot>
    );
}

const useFilterSelectWithCheckboxesStyles = tss
    .withNestedSelectors<'root'>()
    .create(({ theme, classes }) => ({
        root: {},
        label_options: {
            '& + span': {
                marginLeft: 24,
                color: getThemeColor(theme)('gray'),
                fontSize: 12,
            },
            // Hide response-count inside chips. Might be better to do a JS solution (why render something we should hide)
            [`.${classes.root} & + span`]: {
                display: 'none',
            },
            wordWrap: 'break-word',
            maxWidth: 600,
            whiteSpace: 'normal',
            display: 'inline-block',
        },
        searchBarMenuItem: {
            position: 'sticky',
            top: 0,
            zIndex: 5,
            padding: '15px 25px',
            outline: 0,
            backgroundColor: theme.palette.background.paper,
            backgroundImage: darkModeBackgroundImageElevation(theme, 8),
        },
        searchBarActions: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'start',
            gap: theme.spacing(2),
        },
        // searchBarAction: {
        //     fontSize: 14,
        //     fontWeight: 'bold',
        //     textDecoration: 'underline',
        //     color: getThemeColor(theme)('black'),
        //     textTransform: 'uppercase',
        //     outline: 0,
        //     border: 0,
        //     background: 'none',
        //     cursor: 'pointer',
        // },
        menu: {
            height: 285,
        },
        noItemsFound: {
            textAlign: 'center',
            padding: '15px 25px',
        },
    }));

export default function FilterSelectWithCheckboxes({
    options,
    values,
    onChange,
    label,
    onDeleteValue,
    chooseAllValues,
    clearValues,
    searchTextFieldLabel = 'Search',
}: {
    options: SelectOptions;
    values: Array<string>;
    onChange: SelectProps<string[]>['onChange'];
    label: string;
    onDeleteValue: (option: SelectOption) => void;
    chooseAllValues: (options: SelectOptions) => void;
    clearValues: (options: SelectOptions) => void;
    searchTextFieldLabel?: string;
}) {
    const { classes } = useFilterSelectWithCheckboxesStyles();
    // Use for opening the menu window / popup.
    const [isOpen, setIsOpen] = React.useState(false);

    // For search options with filter.
    const [search, setSearch] = React.useState('');

    const filteredOptions = React.useMemo(() => {
        // This `matchSorter` will filter the values and automatically do sort.
        // We use this library - https://github.com/kentcdodds/match-sorter
        return matchSorter(options, search, { keys: ['label'] });
    }, [options, search]);

    // Use for popup window.
    function handleClose() {
        setIsOpen(false);
        // clear the search key
        setSearch('');
    }

    return (
        <MultipleSelect
            isOpen={isOpen}
            onOpen={() => setIsOpen(true)}
            onClose={handleClose}
            options={options}
            values={values}
            label={label}
            onChange={onChange}
            handleClose={handleClose}
            onDeleteValue={onDeleteValue}
            MenuProps={{
                // typescript seems to require that this is present here
                open: isOpen,
                classes: {
                    paper: classes.menu,
                },
                MenuListProps: {
                    autoFocusItem: false,
                },
            }}
        >
            <li
                className={classes.searchBarMenuItem}
                onClick={(event) => event.preventDefault()}
            >
                <SearchBar
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={searchTextFieldLabel}
                >
                    <div className={classes.searchBarActions}>
                        <Button
                            onClick={() => chooseAllValues(filteredOptions)}
                        >
                            <FormattedMessage defaultMessage="Choose all" />
                        </Button>
                        <Button onClick={() => clearValues(filteredOptions)}>
                            <FormattedMessage defaultMessage="Clear all" />
                        </Button>
                    </div>
                </SearchBar>
            </li>
            {filteredOptions.length === 0 ? (
                <li className={classes.noItemsFound}>
                    <FormattedMessage defaultMessage="No found item" />
                </li>
            ) : (
                filteredOptions.map((o: any) => (
                    <MenuItem key={o.value} value={o.value}>
                        <Checkbox checked={values.indexOf(o.value) > -1} />
                        <ListItemText
                            primary=<span className={classes.label_options}>
                                {o.label}
                            </span>
                        />
                    </MenuItem>
                ))
            )}
        </MultipleSelect>
    );
}
